import React, { Suspense, useState } from "react";
import {
  Info,
  Lightbulb,
  AlertTriangle,
  AlertOctagon,
  Copy,
  Check,
} from "lucide-react";
import { mockupComponents } from "./mockups";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert heading text → URL-safe id */
function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** Extract all headings from a content array for TOC use */
export function extractHeadings(content) {
  if (!Array.isArray(content)) return [];
  return content
    .filter((block) => block.type === "heading")
    .map((block) => ({
      text: block.text,
      level: block.level,
      id: slugify(block.text),
    }));
}

// ---------------------------------------------------------------------------
// Role colours
// ---------------------------------------------------------------------------
const ROLE_COLORS = {
  admin: { bg: "#EEF2FF", text: "#4B7BF5", border: "#C7D2FE" },
  student: { bg: "#ECFDF5", text: "#10B981", border: "#A7F3D0" },
  faculty: { bg: "#F5F3FF", text: "#8B5CF6", border: "#DDD6FE" },
  parent: { bg: "#FFFBEB", text: "#F59E0B", border: "#FDE68A" },
  hr_manager: { bg: "#FFF1F2", text: "#F43F5E", border: "#FECDD3" },
  placement_officer: { bg: "#ECFEFF", text: "#06B6D4", border: "#A5F3FC" },
};

// ---------------------------------------------------------------------------
// Callout config
// ---------------------------------------------------------------------------
const CALLOUT_CONFIG = {
  info: {
    color: "#4B7BF5",
    bg: "rgba(75,123,245,0.08)",
    Icon: Info,
  },
  tip: {
    color: "#10B981",
    bg: "rgba(16,185,129,0.08)",
    Icon: Lightbulb,
  },
  // "success" maps to tip styling
  success: {
    color: "#10B981",
    bg: "rgba(16,185,129,0.08)",
    Icon: Lightbulb,
  },
  warning: {
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.08)",
    Icon: AlertTriangle,
  },
  danger: {
    color: "#EF4444",
    bg: "rgba(239,68,68,0.08)",
    Icon: AlertOctagon,
  },
};

// ---------------------------------------------------------------------------
// Block renderers
// ---------------------------------------------------------------------------

function HeadingBlock({ block }) {
  const id = slugify(block.text);
  const baseClass =
    "font-['Syne'] font-bold scroll-mt-24 text-[#0F1B3D] mt-8 mb-3";
  if (block.level === 2) {
    return (
      <h2 id={id} className={`${baseClass} text-xl`}>
        {block.text}
      </h2>
    );
  }
  return (
    <h3 id={id} className={`${baseClass} text-lg`}>
      {block.text}
    </h3>
  );
}

function TextBlock({ block }) {
  return (
    <p
      className="font-['DM_Sans'] text-[#475569] mb-4"
      style={{ fontSize: 15, lineHeight: 1.8 }}
    >
      {block.body}
    </p>
  );
}

function CalloutBlock({ block }) {
  const config = CALLOUT_CONFIG[block.variant] || CALLOUT_CONFIG.info;
  const { color, bg, Icon } = config;
  return (
    <div
      className="rounded-lg px-4 py-3 mb-4 border-l-4"
      style={{ background: bg, borderColor: color }}
    >
      <div className="flex items-start gap-2">
        <Icon
          size={17}
          className="mt-0.5 shrink-0"
          style={{ color }}
        />
        <div>
          {block.title && (
            <p
              className="font-['DM_Sans'] font-semibold mb-0.5"
              style={{ color, fontSize: 14 }}
            >
              {block.title}
            </p>
          )}
          <p
            className="font-['DM_Sans'] text-[#475569] m-0"
            style={{ fontSize: 14, lineHeight: 1.7 }}
          >
            {block.body}
          </p>
        </div>
      </div>
    </div>
  );
}

function StepsBlock({ block }) {
  return (
    <div className="mb-6">
      {block.items.map((item, idx) => {
        const isLast = idx === block.items.length - 1;
        return (
          <div key={idx} className="flex gap-4">
            {/* Left column: circle + connector */}
            <div className="flex flex-col items-center">
              <div
                className="flex items-center justify-center rounded-full text-white font-['Syne'] font-bold shrink-0"
                style={{
                  width: 28,
                  height: 28,
                  background: "#4B7BF5",
                  fontSize: 13,
                }}
              >
                {idx + 1}
              </div>
              {!isLast && (
                <div
                  className="flex-1 mt-1"
                  style={{
                    width: 1,
                    background: "#E2E8F0",
                    minHeight: 24,
                  }}
                />
              )}
            </div>
            {/* Right column: content */}
            <div className={`pb-6 ${isLast ? "pb-0" : ""}`}>
              <p
                className="font-['DM_Sans'] font-semibold text-[#0F1B3D] mt-0.5 mb-1"
                style={{ fontSize: 15 }}
              >
                {item.title}
              </p>
              {item.description && (
                <p
                  className="font-['DM_Sans'] text-[#475569]"
                  style={{ fontSize: 14, lineHeight: 1.7 }}
                >
                  {item.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ListBlock({ block }) {
  return (
    <ul className="mb-4 space-y-1.5 pl-1">
      {block.items.map((item, idx) => (
        <li key={idx} className="flex items-start gap-2.5">
          <span
            className="mt-[7px] rounded-full shrink-0"
            style={{ width: 6, height: 6, background: "#4B7BF5" }}
          />
          <span
            className="font-['DM_Sans'] text-[#475569]"
            style={{ fontSize: 15, lineHeight: 1.7 }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function MockupBlock({ block }) {
  const LazyComponent = mockupComponents[block.id];
  if (!LazyComponent) {
    return (
      <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center h-48 mb-6">
        <p className="font-['DM_Sans'] text-[#94A3B8] text-sm">
          Mockup: {block.id}
        </p>
      </div>
    );
  }
  return (
    <div className="mb-6">
      <Suspense
        fallback={
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-center h-48 animate-pulse">
            <p className="font-['DM_Sans'] text-[#94A3B8] text-sm">
              Loading…
            </p>
          </div>
        }
      >
        <LazyComponent />
      </Suspense>
    </div>
  );
}

function RoleBadgeBlock({ block }) {
  // Support two shapes:
  // 1. roles: ["admin", "student"] — simple string array
  // 2. roles: [{ role, label, description }] — object array with detail
  const roles = block.roles || [];
  const isSimple = typeof roles[0] === "string";

  if (isSimple) {
    return (
      <div className="flex flex-wrap gap-2 mb-4">
        {roles.map((role) => {
          const colors = ROLE_COLORS[role] || {
            bg: "#F1F5F9",
            text: "#64748B",
            border: "#CBD5E1",
          };
          return (
            <span
              key={role}
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-['DM_Sans'] font-semibold border"
              style={{
                background: colors.bg,
                color: colors.text,
                borderColor: colors.border,
              }}
            >
              {role.replace(/_/g, " ")}
            </span>
          );
        })}
      </div>
    );
  }

  // Object form — render cards
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
      {roles.map((item) => {
        const colors = ROLE_COLORS[item.role] || {
          bg: "#F1F5F9",
          text: "#64748B",
          border: "#CBD5E1",
        };
        return (
          <div
            key={item.role}
            className="rounded-lg border p-3"
            style={{ borderColor: colors.border, background: colors.bg }}
          >
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-['DM_Sans'] font-bold mb-1.5 border"
              style={{
                color: colors.text,
                borderColor: colors.border,
                background: "white",
              }}
            >
              {item.label || item.role.replace(/_/g, " ")}
            </span>
            {item.description && (
              <p
                className="font-['DM_Sans'] text-[#475569] m-0"
                style={{ fontSize: 13, lineHeight: 1.6 }}
              >
                {item.description}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TableBlock({ block }) {
  // Support both "headers" and "columns" keys (data layer uses "columns")
  const headers = block.headers || block.columns || [];
  const rows = block.rows || [];
  return (
    <div className="mb-6 overflow-x-auto rounded-lg border border-[#E2E8F0]">
      <table className="w-full text-sm font-['DM_Sans']">
        <thead>
          <tr style={{ background: "#F1F5F9" }}>
            {headers.map((h, i) => (
              <th
                key={i}
                className="px-4 py-2.5 text-left font-semibold text-[#0F1B3D] border-b border-[#E2E8F0] whitespace-nowrap"
                style={{ fontSize: 13 }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr
              key={ri}
              className={ri % 2 === 1 ? "bg-[#F8FAFC]" : "bg-white"}
            >
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="px-4 py-2.5 text-[#475569] border-b border-[#E2E8F0] last:border-b-0"
                  style={{ fontSize: 13, lineHeight: 1.6 }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlock({ block }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(block.code || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl mb-6 overflow-hidden border border-[#1E2D5A]">
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ background: "#0F1B3D" }}
      >
        <span
          className="font-['JetBrains_Mono'] text-xs text-[#8892B0]"
          style={{ letterSpacing: "0.05em" }}
        >
          {block.language || "code"}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-[#8892B0] hover:text-white transition-colors"
        >
          {copied ? (
            <>
              <Check size={13} />
              <span className="font-['DM_Sans']">Copied</span>
            </>
          ) : (
            <>
              <Copy size={13} />
              <span className="font-['DM_Sans']">Copy</span>
            </>
          )}
        </button>
      </div>
      {/* Code area */}
      <pre
        className="overflow-x-auto p-4 m-0"
        style={{ background: "#0F1B3D" }}
      >
        <code
          className="font-['JetBrains_Mono'] text-[#CDD6F4]"
          style={{ fontSize: 13, lineHeight: 1.75 }}
        >
          {block.code}
        </code>
      </pre>
    </div>
  );
}

function DividerBlock() {
  return <hr className="border-[#E2E8F0] my-8" />;
}

// ---------------------------------------------------------------------------
// Main renderer
// ---------------------------------------------------------------------------

function renderBlock(block, idx) {
  switch (block.type) {
    case "heading":
      return <HeadingBlock key={idx} block={block} />;
    case "text":
      return <TextBlock key={idx} block={block} />;
    case "callout":
      return <CalloutBlock key={idx} block={block} />;
    case "steps":
      return <StepsBlock key={idx} block={block} />;
    case "list":
      return <ListBlock key={idx} block={block} />;
    case "mockup":
      return <MockupBlock key={idx} block={block} />;
    case "role-badge":
      return <RoleBadgeBlock key={idx} block={block} />;
    case "table":
      return <TableBlock key={idx} block={block} />;
    case "code":
      return <CodeBlock key={idx} block={block} />;
    case "divider":
      return <DividerBlock key={idx} />;
    default:
      return null;
  }
}

export default function DocsRenderer({ content }) {
  if (!Array.isArray(content) || content.length === 0) return null;
  return (
    <div className="docs-renderer">
      {content.map((block, idx) => renderBlock(block, idx))}
    </div>
  );
}
